import React, { useState, useMemo, useCallback } from 'react';
import { getAppVersion } from '../services/api';
import useAutoRefresh from '../hooks/useAutoRefresh';
import './UpdateBanner.css';

// Deploy-version beacon (client side). Stale open tabs repeatedly made
// deployed fixes look broken (order-unlink rendering, claim staleness) —
// the tab kept running last week's bundle until someone thought to
// hard-refresh. This banner polls /api/version (the backend reports the
// hash of the bundle currently on the server's disk) and compares it with
// the hash of the bundle THIS tab is actually running; on mismatch it
// offers a one-click refresh. Mounted once, App-level, above the router.
//
// Replaces the earlier <UpdatePrompt/>, which re-fetched and parsed
// /index.html for the same purpose. Asking the backend is sturdier (no
// HTML parsing, /api is never cached by nginx) and this one is
// dismissible — per-version, so dismissing today's deploy still lets next
// week's fire.

// The hash of the running bundle comes from the <script src=".../static/
// js/main.<hash>.js"> tag CRA injected into the page we booted from — no
// build-time env var needed, so both tenants get it for free. Returns null
// under the webpack dev server (bundle.js has no hash): the beacon then
// disables itself, which is what we want in dev.
const getRunningVersion = () => {
  for (const script of document.scripts) {
    const m = script.src && script.src.match(/\/static\/js\/main\.([0-9a-f]+)\.js/i);
    if (m) return m[1];
  }
  return null;
};

const POLL_INTERVAL_MS = 5 * 60 * 1000;

function UpdateBanner() {
  const runningVersion = useMemo(getRunningVersion, []);
  const [servedVersion, setServedVersion] = useState(null);
  const [dismissedVersion, setDismissedVersion] = useState(null);

  const tick = useCallback(async () => {
    try {
      const data = await getAppVersion();
      if (data && data.version) setServedVersion(data.version);
    } catch (e) {
      // Offline / server restarting mid-deploy — stay quiet, next tick retries.
    }
  }, []);

  // Poll while visible + on focus/visibility return; disabled entirely in
  // dev where there is no hashed bundle to compare against.
  useAutoRefresh(tick, { intervalMs: POLL_INTERVAL_MS, enabled: !!runningVersion });

  // Only fire when BOTH sides are known and disagree.
  const show =
    runningVersion &&
    servedVersion &&
    servedVersion !== runningVersion &&
    servedVersion !== dismissedVersion;

  if (!show) return null;

  return (
    <div className="update-banner" role="status">
      <span className="update-banner-text">
        A new version of the app is available.
      </span>
      <button
        type="button"
        className="update-banner-refresh"
        onClick={() => window.location.reload()}
      >
        Refresh
      </button>
      <button
        type="button"
        className="update-banner-dismiss"
        aria-label="Dismiss"
        title="Dismiss"
        onClick={() => setDismissedVersion(servedVersion)}
      >
        ×
      </button>
    </div>
  );
}

export default UpdateBanner;
